/**
 * Re-reads the case's attachment list after a signature until the archived
 * document actually shows up.
 *
 * @author Milton Filho
 */
import { EMPTY, merge, timer } from 'rxjs';
import {
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  takeWhile,
} from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { CustomEpic, PayloadAction } from 'types/types';
import {
  fetchAppFiles,
  FetchAppFilesActionPayload,
  RequestFormSuccessPayload,
} from '../actions';
import {
  FETCH_APP_FILES_START,
  REQUEST_FORM,
  REQUEST_FORM_SUCCESS,
} from '../actionTypes';
import { getCaseFile } from 'selectors/files/getCaseFile';
import { getFilesFetchStatus } from 'selectors/files/getFilesFetchStatus';
import { FetchStatus } from 'types/types';
import {
  CASE_FILES_POLL_DEADLINE_MS,
  CASE_FILES_POLL_INTERVAL_MS,
  CASE_FILES_POLL_MAX_ATTEMPTS,
} from '../constants';

/**
 * Waits for the signed document to be archived, then stops.
 *
 * `requestForm$` dispatches one `fetchAppFiles` the instant the embed reports a
 *   signature. That request is not wrong, it is just always too early: the
 *   backend measured 16.5 s and 14.7 s from the patient's click to the
 *   `case_files3` row, so the list comes back without the new document every
 *   single time. Nothing else re-read it, so the screen stayed stale until it
 *   remounted — for minutes, not for 16 seconds.
 *
 * Why this matters beyond a stale list: `CaseFileControls` decides between
 *   "Sign" and "Re-send" from exactly this data, and the next step is to stop
 *   sending `resend: true` unconditionally. Without this epic that step would
 *   infer `resend: false` right after a signature and the therapist would be
 *   told the form is already on file — about the signature they just collected.
 *   So this has to land first.
 *
 * **The signal is identity, not presence.** A re-send already has an archived
 *   document, so "a file exists for this type" is true before the poll starts
 *   and would end it immediately. `previousFileId` is captured at signing time
 *   and the poll runs until the current `fileId` differs from it.
 *
 * Stopping early matters as much as polling. The `FETCH_APP_FILES_SUCCESS`
 *   reducer replaces the whole case map with the one case it fetched —
 *   deliberately, "for security reasons" — so a poll still running after the
 *   therapist opened a different case would blank that case's files. Any fetch
 *   for another case therefore ends this one, and so does a new signing.
 */
export const pollCaseFilesAfterSigning$: CustomEpic<
  RequestFormSuccessPayload
> = (action$, state$) =>
  action$.pipe(
    ofType(REQUEST_FORM_SUCCESS),
    // A second signature supersedes the first: its own poll re-reads the same
    //   list, so nothing is lost by dropping the earlier one.
    switchMap(({ payload }) => {
      const { caseId, appFileType, previousFileId } = payload || {};
      if (!caseId || !appFileType) return EMPTY;

      // Guarded for the same reason as the read in `requestForm$`: `getFiles`
      //   dereferences `state.files` without checking it.
      // Guarded like `isArchived`: `state.files` is dereferenced without a
      //   check by the selectors that read it.
      const isFetching = () =>
        !!state$.value?.files &&
        getFilesFetchStatus(state$.value) === FetchStatus.Fetching;

      const isArchived = () => {
        if (!state$.value?.files) return false;
        const file = getCaseFile(state$.value, { caseId, appFileType });
        return !!file?.fileId && file.fileId !== previousFileId;
      };

      // Already there — the provider's callback beat us, which is possible and
      //   is the outcome the poll is waiting for anyway.
      if (isArchived()) return EMPTY;

      const stop$ = merge(
        action$.pipe(
          ofType(FETCH_APP_FILES_START),
          // `ofType` narrows against this epic's own payload type, so the
          //   foreign action has to be re-read explicitly.
          filter(
            action =>
              (action as PayloadAction<FetchAppFilesActionPayload>).payload
                ?.caseId !== caseId,
          ),
        ),
        action$.pipe(ofType(REQUEST_FORM)),
      );

      // Starts at one interval in, because `requestForm$` has just fired the
      //   immediate read this epic is compensating for.
      return timer(
        CASE_FILES_POLL_INTERVAL_MS,
        CASE_FILES_POLL_INTERVAL_MS,
      ).pipe(
        takeWhile(() => !isArchived()),
        // Never dispatch while a read is already in flight.
        //
        // `fetchAppFiles$` maps over its input with `switchMap`, so every new
        //   `FETCH_APP_FILES_START` cancels the request the previous one
        //   started. A blind tick every 2.5 s is therefore safe only while
        //   reads come back faster than that. Once they do not — a therapist on
        //   cellular is enough — each tick kills its predecessor and only the
        //   final attempt ever completes, which turns this epic back into the
        //   single delayed read it was written to replace.
        //
        // Skipping instead of cancelling costs nothing: the answer that is
        //   already on its way is the same answer the skipped tick would have
        //   asked for.
        filter(() => !isFetching()),
        // After the filter, so the budget counts attempts that actually
        //   happened rather than ticks that were skipped.
        take(CASE_FILES_POLL_MAX_ATTEMPTS),
        map(() => fetchAppFiles({ caseId })),
        takeUntil(merge(stop$, timer(CASE_FILES_POLL_DEADLINE_MS))),
      );
    }),
  );
